import { Injectable, OnDestroy, Inject, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { DebugLoggerService } from './helpers/debug-logger.service';

export interface WebSocketEvent {
  event: string;
  data: unknown;
  timestamp: Date;
}

export interface QuoteUpdateEvent {
  quoteId?: number;
  exportId?: string;
  type?: 'quote_updated' | 'quote_created' | 'quote_status_changed' | 'quote_product_updated' | string;
  data?: unknown;
  userId?: number;
  socketId?: string;
}

export interface OrderUpdateEvent {
  orderId?: number;
  exportId?: string;
  type: 'order_created' | 'order_updated';
  data: unknown;
  userId?: number;
  socketId?: string;
}

export interface SampleRequestEvent {
  sampleRequestId?: number;
  companyId?: number;
  data: unknown;
  userId?: number;
  socketId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly isBrowser: boolean;

  // Debug mode flag - set to true to enable detailed logging
  private readonly debugMode: boolean = false;

  // Connection state management
  private connectionState$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  // Event streams
  private quoteEvents$ = new Subject<QuoteUpdateEvent>();
  private quoteCreatedEvents$ = new Subject<QuoteUpdateEvent>();
  private orderEvents$ = new Subject<OrderUpdateEvent>();
  private sampleRequestEvents$ = new Subject<SampleRequestEvent>();
  private generalEvents$ = new Subject<WebSocketEvent>();

  // Room management
  private currentRooms = new Set<string>();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private debugLogger: DebugLoggerService,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.log('WebSocketService initialized');
    this.initializeConnection();
  }

  /**
   * Re-enters the Angular zone for each emission of `source`.
   *
   * The socket is created outside the zone (see initializeConnection), so its callbacks - and any
   * Subject they push into - run outside it too and would not trigger change detection. Rather than
   * wrapping every `.next()`, every public stream is passed through here so subscribers always get
   * their values inside the zone.
   */
  private runInZone<T>(source: Observable<T>): Observable<T> {
    return new Observable<T>(subscriber => source.subscribe({
      next: value => this.ngZone.run(() => subscriber.next(value)),
      error: error => this.ngZone.run(() => subscriber.error(error)),
      complete: () => this.ngZone.run(() => subscriber.complete())
    }));
  }

  /**
   * Logs debug messages when debug mode is enabled
   * @param message - The message to log
   * @param data - Optional data to log
   */
  private log(message: string, data?: any): void {
    if (this.debugMode) {
      if (data !== undefined) {
        this.debugLogger.log(`[WebSocket Debug] ${message}`, data);
      } else {
        this.debugLogger.log(`[WebSocket Debug] ${message}`);
      }
    }
  }

  /**
   * Initialize WebSocket connection
   * Skips connection during server-side rendering
   *
   * Everything socket.io schedules has to stay outside the Angular zone. engine.io keeps a
   * ping-timeout timer alive that re-arms itself after every heartbeat, and the 'polling' fallback
   * transport holds a long-poll XHR open; zone.js patches both as macrotasks, so an in-zone socket
   * means hasPendingMacrotasks is true for as long as the connection lives, ApplicationRef
   * .isStable() never emits and hydration fails with NG0506 on every page that injects this service.
   * Subscribers still get change detection - the public streams re-enter the zone via runInZone().
   */
  private initializeConnection(): any {
    // Skip WebSocket connection during server-side rendering
    if (!this.isBrowser) {
      this.log('Skipping WebSocket connection during SSR');
      return;
    }

    if (this.socket?.connected) {
      this.log('Socket already connected, skipping initialization');
      return;
    }

    this.log('Initializing WebSocket connection', {url: environment.socketUrl});
    this.connectionState$.next('connecting');

    const socketOptions: {
      transports: string[];
      timeout: number;
      forceNew: boolean;
      reconnection: boolean;
      reconnectionAttempts: number;
      reconnectionDelay: number;
    } = {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    };

    this.ngZone.runOutsideAngular(() => {
      this.socket = io(environment.socketUrl, socketOptions);
      this.setupEventListeners();
    });
  }

  /**
   * Setup Socket.IO event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.log('Setting up WebSocket event listeners');

    // Connection events
    this.socket.on('connect', () => {
      this.log('WebSocket connected', {socketId: this.socket?.id});
      this.connectionState$.next('connected');
      this.reconnectAttempts = 0;
      this.rejoinRooms();
    });

    this.socket.on('disconnect', (reason: string) => {
      this.log('WebSocket disconnected', {reason});
      this.connectionState$.next('disconnected');
      this.currentRooms.clear();
    });

    this.socket.on('connect_error', (error: Error) => {
      this.log('WebSocket connection error', {error: error.message});
      this.connectionState$.next('error');
      this.handleReconnection();
    });

    // Quote-specific events
    this.socket.on('quote_updated', (data: QuoteUpdateEvent) => {
      this.log('quote_updated event received', {
        socketId: data.socketId,
        currentSocketId: this.socket?.id,
        willProcess: this.socket?.id !== data.socketId,
        data
      });
      if (this.socket?.id !== data.socketId) {
        this.log('Processing quote_updated event (different socket)', data);
        this.quoteEvents$.next(data);
      } else {
        this.log('Ignoring quote_updated event (same socket)');
      }
    });

    this.socket.on('quote_created', (data: QuoteUpdateEvent) => {
      this.log('quote_created event received', {
        socketId: data.socketId,
        currentSocketId: this.socket?.id,
        userId: data.userId,
        willProcess: this.socket?.id !== data.socketId,
        data
      });
      // Filter out events from the same socket (own actions)
      if (this.socket?.id !== data.socketId) {
        this.log('Processing quote_created event (different socket)', data);
        // Emit to both general quote events and specific quote created events
        this.quoteEvents$.next(data);
        this.quoteCreatedEvents$.next(data);
      } else {
        this.log('Ignoring quote_created event (same socket)');
      }
    });

    // Sample request events
    this.socket.on('sample_request_created', (data: SampleRequestEvent) => {
      this.log('sample_request_created event received', {
        socketId: data.socketId,
        currentSocketId: this.socket?.id,
        willProcess: this.socket?.id !== data.socketId,
        data
      });
      // Filter out events from the same socket (own actions)
      if (this.socket?.id !== data.socketId) {
        this.log('Processing sample_request_created event (different socket)', data);
        this.sampleRequestEvents$.next(data);
      } else {
        this.log('Ignoring sample_request_created event (same socket)');
      }
    });

    // Order-specific events
    // ✅ No socket ID check - emit to all subscribers including same socket
    // This allows order list to refresh even when updating from order detail in same browser
    this.socket.on('order_created', (data: OrderUpdateEvent) => {
      this.orderEvents$.next(data);
    });

    // Room management events
    this.socket.on('room_joined', (data: { room: string }) => {
      this.log('Joined room', {room: data.room, totalRooms: this.currentRooms.size + 1});
      this.currentRooms.add(data.room);
    });

    this.socket.on('room_left', (data: { room: string }) => {
      this.log('Left room', {room: data.room, totalRooms: this.currentRooms.size - 1});
      this.currentRooms.delete(data.room);
    });
  }

  /**
   * Handle reconnection logic with exponential backoff
   */
  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('Max reconnection attempts reached', {attempts: this.reconnectAttempts});
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    this.log(`Attempting to reconnect`, {delay, attempt: this.reconnectAttempts, maxAttempts: this.maxReconnectAttempts});

    // Outside the zone as well: a retry pending in-zone is itself a macrotask blocking stability.
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initializeConnection();
      }, delay);
    });
  }

  /**
   * Rejoin all rooms after reconnection
   */
  private rejoinRooms(): void {
    this.log('Rejoining rooms after reconnection', {roomCount: this.currentRooms.size, rooms: Array.from(this.currentRooms)});
    this.currentRooms.forEach(room => {
      this.joinRoom(room);
    });
  }

  /**
   * Join a specific room (quote-based or export-based)
   */
  public joinRoom(roomId: string): void {
    if (!this.socket?.connected) {
      this.log('Cannot join room: WebSocket not connected', {roomId});
      return;
    }

    // Check if already in the room to prevent duplicate joins
    if (this.currentRooms.has(roomId)) {
      this.log('Already in room', {roomId});
      return;
    }

    this.log('Joining room', {roomId, socketId: this.socket.id});
    this.socket.emit('join_room', { room: roomId });
    this.currentRooms.add(roomId);
  }

  /**
   * Leave a specific room
   */
  public leaveRoom(roomId: string): void {
    if (!this.socket?.connected) {
      this.log('Cannot leave room: WebSocket not connected', {roomId});
      return;
    }

    this.log('Leaving room', {roomId, socketId: this.socket.id});
    this.socket.emit('leave_room', { room: roomId });
    this.currentRooms.delete(roomId);
  }

  /**
   * Get connection state observable
   */
  public getConnectionState(): Observable<'disconnected' | 'connecting' | 'connected' | 'error'> {
    return this.runInZone(this.connectionState$.asObservable());
  }

  /**
   * Get quote events observable
   */
  public getQuoteEvents(): Observable<QuoteUpdateEvent> {
    return this.runInZone(this.quoteEvents$.asObservable());
  }

  /**
   * Get quote created events observable
   * Specifically for listening to new quote creation events
   */
  public getQuoteCreatedEvents(): Observable<QuoteUpdateEvent> {
    return this.runInZone(this.quoteCreatedEvents$.asObservable());
  }

  /**
   * Get order events observable
   */
  public getOrderEvents(): Observable<OrderUpdateEvent> {
    return this.runInZone(this.orderEvents$.asObservable());
  }

  /**
   * Get sample request events observable
   */
  public getSampleRequestEvents(): Observable<SampleRequestEvent> {
    return this.runInZone(this.sampleRequestEvents$.asObservable());
  }

  /**
   * Get general events observable
   */
  public getGeneralEvents(): Observable<WebSocketEvent> {
    return this.runInZone(this.generalEvents$.asObservable());
  }

  /**
   * Emit a custom event to the server
   */
  public emit(event: string, data: unknown): void {
    if (!this.socket?.connected) {
      this.log('Cannot emit event: WebSocket not connected', {event, data});
      return;
    }

    // Add socket ID to the data to identify the sender
    const dataWithSocketId = {
      ...data as object,
      socketId: this.socket.id
    };

    this.log('Emitting event', {event, socketId: this.socket.id, data: dataWithSocketId});
    this.socket.emit(event, dataWithSocketId);
  }

  /**
   * Check if currently connected
   */
  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current socket ID
   */
  public getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Get current rooms
   */
  public getCurrentRooms(): string[] {
    return Array.from(this.currentRooms);
  }

  /**
   * Manually reconnect
   */
  public reconnect(): void {
    if (!this.isBrowser) {
      this.log('Skipping WebSocket reconnect during SSR');
      return;
    }

    this.log('Manual reconnect triggered');
    if (this.socket) {
      this.socket.disconnect();
    }
    this.reconnectAttempts = 0;
    this.initializeConnection();
  }

  /**
   * Disconnect WebSocket
   */
  public disconnect(): void {
    if (this.socket) {
      this.log('Disconnecting WebSocket', {roomCount: this.currentRooms.size});
      this.currentRooms.clear();
      this.socket.disconnect();
      this.socket = null;
      this.connectionState$.next('disconnected');
    }
  }

  /**
   * Cleanup on service destruction
   */
  public ngOnDestroy(): void {
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
